import { arrayRemove, arrayUnion, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { deleteObject, ref, uploadBytes } from "firebase/storage";
import { db, storage, storageBucket } from "../firebase.js";
import { fetchImageEntriesByPath, normalizeStoragePath } from "../features/photos.jsx";

function sanitizeFileName(fileName) {
  return fileName.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9._-]/g, "");
}

function createGsPath(storageRef) {
  return `gs://${storageBucket}/${storageRef.fullPath}`;
}

export async function loadGalleryEntries(documentPath) {
  return fetchImageEntriesByPath(documentPath);
}

export async function uploadGalleryFiles({
  documentPath,
  files,
  replaceExisting = false,
  storageDirectory,
}) {
  if (!files.length) {
    return [];
  }

  const uploadedPaths = [];

  for (const [index, file] of files.entries()) {
    const timestamp = Date.now();
    const safeName = sanitizeFileName(file.name || `upload-${index + 1}.jpg`);
    const storagePath = `${storageDirectory}/${timestamp}-${index + 1}-${safeName}`;
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, file);
    uploadedPaths.push(createGsPath(storageRef));
  }

  if (replaceExisting) {
    const existingEntries = await fetchImageEntriesByPath(documentPath);

    await Promise.all(
      existingEntries.map(async (entry) => {
        const storagePath = normalizeStoragePath(entry.path);

        if (!storagePath) {
          return;
        }

        try {
          await deleteObject(ref(storage, storagePath));
        } catch (error) {
          if (error.code !== "storage/object-not-found") {
            throw error;
          }
        }
      })
    );

    await setDoc(
      doc(db, documentPath),
      {
        Images: uploadedPaths,
      },
      { merge: true }
    );
  } else {
    await setDoc(
      doc(db, documentPath),
      {
        Images: arrayUnion(...uploadedPaths),
      },
      { merge: true }
    );
  }

  return fetchImageEntriesByPath(documentPath);
}

export async function deleteGalleryImage({
  documentPath,
  imagePath,
}) {
  const storagePath = normalizeStoragePath(imagePath);

  if (storagePath) {
    try {
      await deleteObject(ref(storage, storagePath));
    } catch (error) {
      if (error.code !== "storage/object-not-found") {
        throw error;
      }
    }
  }

  await updateDoc(doc(db, documentPath), {
    Images: arrayRemove(imagePath),
  });

  return fetchImageEntriesByPath(documentPath);
}

export async function setGalleryStartImage({
  documentPath,
  imagePath,
}) {
  const galleryRef = doc(db, documentPath);
  const gallerySnapshot = await getDoc(galleryRef);

  if (!gallerySnapshot.exists()) {
    throw new Error("We could not find this gallery.");
  }

  const galleryData = gallerySnapshot.data() || {};
  const imageFieldName =
    ["Images", "images", "Photos", "photos", "Image", "image", "Photo", "photo"].find(
      (fieldName) =>
        Array.isArray(galleryData[fieldName]) || typeof galleryData[fieldName] === "string"
    ) || "Images";
  const currentImages = Array.isArray(galleryData[imageFieldName])
    ? galleryData[imageFieldName]
    : typeof galleryData[imageFieldName] === "string"
      ? [galleryData[imageFieldName]]
      : [];

  if (!currentImages.includes(imagePath)) {
    throw new Error("We could not find that photo in this gallery.");
  }

  await setDoc(
    galleryRef,
    {
      [imageFieldName]: [
        imagePath,
        ...currentImages.filter((currentPath) => currentPath !== imagePath),
      ],
    },
    { merge: true }
  );

  return fetchImageEntriesByPath(documentPath);
}
